import { z } from "zod";
import { specialtySchema, type Specialty } from "./branding";
import { countryCode, currencyCode, email, text, timeZone, uuid } from "./common";

/** Platform admin: provision a new clinic tenant with its owner and first branch. */
export const createTenantInput = z.object({
  name: text(200),
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])$/, "3-48 lowercase letters, digits or dashes"),
  countryCode,
  currencyCode,
  timeZone,
  locale: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/),
  /** Dental or Ayurveda edition. Existing integrations that omit it get a dental clinic. */
  specialty: specialtySchema.default("dental"),
  planId: uuid,
  owner: z.object({ email, fullName: text(200) }),
  firstBranch: z.object({ code: z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9-]{0,19}$/), name: text(200) }),
});
/** Input as sent (specialty may be left out and defaults to dental). */
export type CreateTenantInput = z.input<typeof createTenantInput>;

export const setTenantStatusInput = z.object({
  status: z.enum(["active", "suspended", "cancelled"]),
  reason: z.string().trim().max(500).optional(),
});

export const planInput = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]*$/),
  name: text(100),
  description: z.string().max(1000).optional(),
  trialDays: z.number().int().min(0).max(365).default(14),
  isPublic: z.boolean().default(true),
  /** Price per clinic in INR paise (shown on the product page); null = no price ("contact us"); left out = unchanged. */
  monthlyPriceMinor: z.number().int().min(0).max(100_000_000_00).nullish(),
  yearlyPriceMinor: z.number().int().min(0).max(100_000_000_00).nullish(),
  entitlements: z.array(z.object({ featureKey: z.string(), enabled: z.boolean(), limitValue: z.number().int().min(0).nullable() })),
});

/** Edit a plan: everything except its code. Changes apply at once to every clinic on the plan. */
export const planUpdateInput = planInput.omit({ code: true }).extend({
  trialDays: z.number().int().min(0).max(365),
  isPublic: z.boolean(),
});

export interface TenantAdminView {
  id: string;
  name: string;
  slug: string;
  status: string;
  countryCode: string;
  currencyCode: string;
  timeZone: string;
  createdAt: string;
  plan: { id: string; code: string; name: string } | null;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  signupSource: "platform" | "self_serve";
  suspensionReason: string | null;
  specialty: Specialty;
}

export interface CreateTenantResult {
  tenantId: string;
  ownerUserId: string;
  ownerInviteUrl: string | null;
}

/* ------------------------------------------------------------ self-serve sign-up & trials (Phase 8b) */

const slug = z.string().trim().toLowerCase().regex(/^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])$/, "3-48 lowercase letters, digits or dashes");

/** Public sign-up: the owner proves the email by opening the emailed link (that is where the password is set). */
export const signupInput = z.object({
  clinicName: text(200),
  slug,
  ownerName: text(200),
  email,
  countryCode,
  currencyCode,
  timeZone,
  locale: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/),
  specialty: specialtySchema.default("dental"),
  /** The person ticked "I agree to the terms and privacy notice". */
  acceptTerms: z.literal(true, "Please accept the terms to continue"),
});
export type SignupInput = z.infer<typeof signupInput>;

export interface SignupAvailability {
  open: boolean;
  /** Why sign-up is closed (shown on the page): not switched on, no email provider, no self-serve plan. */
  reason: string | null;
  planName: string | null;
  trialDays: number | null;
}

export const selfServePlanInput = z.object({ planId: uuid.nullable() });

/** Platform: put a clinic on a plan (after an offline payment, or to extend a trial). */
export const setSubscriptionInput = z.object({
  planId: uuid,
  status: z.enum(["trialing", "active"]),
  /** Required for a trial: the day it ends. */
  trialEndsAt: z.iso.datetime({ offset: true }).optional(),
  /** Optional for a paid plan: when the paid period ends. */
  periodEnd: z.iso.datetime({ offset: true }).optional(),
  note: z.string().trim().max(500).optional(),
});

/** A clinic's own plan and usage (Settings → Plan & usage). */
export interface UsageView {
  plan: { name: string; status: string; trialEndsAt: string | null; periodEnd: string | null } | null;
  limits: { featureKey: string; label: string; used: number; limit: number | null; period: "now" | "month" }[];
  features: { featureKey: string; label: string; enabled: boolean }[];
}

/* ------------------------------------------------------------ platform: clinic edit page */

/** Platform admin edits a clinic's basic details (the clinic can change these itself in Settings too). */
export const updateTenantInput = z.object({
  name: text(200),
  countryCode,
  currencyCode,
  timeZone,
  locale: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/),
  email: z.string().trim().toLowerCase().max(254).nullish().transform((v) => v || null).pipe(z.email().nullable()),
  phone: z.string().trim().max(30).nullish().transform((v) => v || null),
});

export interface TenantUserView {
  userId: string;
  membershipId: string;
  fullName: string;
  email: string;
  status: "invited" | "active" | "suspended" | "removed";
  roles: string[];
  lastLoginAt: string | null;
  mfaEnabled: boolean;
  hasPassword: boolean;
}

export interface TenantDetailView extends TenantAdminView {
  locale: string;
  email: string | null;
  phone: string | null;
  branches: { id: string; code: string; name: string; isActive: boolean }[];
  users: TenantUserView[];
  roles: { key: string; name: string }[];
}

/** Platform admin adds a login for someone at the clinic; the invitation link is shown (no email needed). */
export const platformAddUserInput = z.object({ email, fullName: text(200), roleKey: z.string().trim().min(1).max(60) });

export interface SignInLinkResult {
  kind: "invitation" | "password_reset";
  url: string;
  expiresAt: string;
}

/** A plan as shown on the public product page (A6). */
export interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  trialDays: number;
  prices: { period: "month" | "year"; currency: string; amountMinor: number }[];
  /** Features and limits the plan includes (limit null = unlimited, or a feature switch). */
  includes: { feature: string; limit: number | null }[];
}
